import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  ScanText, Copy, Edit, Save, X, RefreshCw, Loader2, CheckCircle, AlertTriangle, Sparkles, UserCog
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: 'tipo_documento_rilevato', label: 'Tipo documento' },
  { key: 'cognome', label: 'Cognome' },
  { key: 'nome', label: 'Nome' },
  { key: 'sesso', label: 'Sesso' },
  { key: 'data_nascita', label: 'Data di nascita' },
  { key: 'luogo_nascita', label: 'Luogo di nascita' },
  { key: 'provincia_nascita', label: 'Provincia di nascita' },
  { key: 'nazionalita', label: "Nazionalita'" },
  { key: 'codice_fiscale', label: 'Codice Fiscale' },
  { key: 'numero_documento', label: 'Numero documento' },
  { key: 'ente_rilascio', label: 'Ente di rilascio' },
  { key: 'data_rilascio', label: 'Data di rilascio' },
  { key: 'data_scadenza', label: 'Data di scadenza' },
  { key: 'categoria_patente', label: 'Categoria patente' },
  { key: 'indirizzo_residenza', label: 'Indirizzo di residenza' },
  { key: 'citta_residenza', label: 'Citta\' di residenza' },
  { key: 'cap_residenza', label: 'CAP di residenza' },
  { key: 'provincia_residenza', label: 'Provincia di residenza' },
];

const CONF_STYLE = {
  alta: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  media: 'bg-amber-100 text-amber-700 border-amber-300',
  bassa: 'bg-rose-100 text-rose-700 border-rose-300',
};

const DocumentOcrCard = ({ doc, onUpdated, clientId, onClientFilled }) => {
  const [ocr, setOcr] = useState(doc.ocr_data || null);
  const [status, setStatus] = useState(doc.ocr_status || 'unknown');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(status === 'processing');

  // Polling if OCR is running (autotrigger from upload)
  useEffect(() => {
    if (!polling) return undefined;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/documents/${doc.id}/ocr`);
        const s = res.data.ocr_status;
        if (s === 'done') {
          setOcr(res.data.ocr_data || {});
          setStatus('done');
          setPolling(false);
          onUpdated && onUpdated();
        } else if (s === 'error') {
          setStatus('error');
          setPolling(false);
        }
      } catch (e) {
        setPolling(false);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [polling, doc.id, onUpdated]);

  const handleTriggerOcr = async () => {
    setBusy(true);
    setStatus('processing');
    setPolling(false);
    try {
      const res = await axios.post(`${API_URL}/api/documents/${doc.id}/ocr`);
      setOcr(res.data.ocr_data);
      setStatus('done');
      toast.success('OCR completato');
      onUpdated && onUpdated();
    } catch (e) {
      setStatus('error');
      toast.error(e.response?.data?.detail || 'Errore OCR');
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = () => {
    setDraft({ ...ocr });
    setEditing(true);
  };

  const handleCancel = () => {
    setDraft({});
    setEditing(false);
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      const res = await axios.put(`${API_URL}/api/documents/${doc.id}/ocr`, { ocr_data: draft });
      setOcr(res.data.ocr_data);
      setEditing(false);
      toast.success('Correzioni salvate');
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setBusy(false);
    }
  };

  const copyToClipboard = (val, label) => {
    if (!val) return;
    navigator.clipboard.writeText(String(val)).then(
      () => toast.success(`${label} copiato`),
      () => toast.error('Copia fallita'),
    );
  };

  const copyAll = () => {
    if (!ocr) return;
    const text = FIELDS
      .filter((f) => ocr[f.key])
      .map((f) => `${f.label}: ${ocr[f.key]}`)
      .join('\n');
    navigator.clipboard.writeText(text).then(
      () => toast.success('Tutti i campi copiati'),
      () => toast.error('Copia fallita'),
    );
  };

  const handleFillClient = async () => {
    if (!clientId || !ocr) return;
    // Mappa OCR -> ClientUpdate. Skippa i campi vuoti/nulli cosi' non sovrascrivi dati esistenti con blank.
    const noteOCR = [];
    if (ocr.data_nascita) noteOCR.push(`Nato il ${ocr.data_nascita}`);
    if (ocr.luogo_nascita) noteOCR.push(`a ${ocr.luogo_nascita}${ocr.provincia_nascita ? ` (${ocr.provincia_nascita})` : ''}`);
    const notePrefill = noteOCR.length ? `[Da documento] ${noteOCR.join(' ')}` : null;

    const payload = {};
    if (ocr.nome) payload.nome = ocr.nome;
    if (ocr.cognome) payload.cognome = ocr.cognome;
    if (ocr.codice_fiscale) payload.codice_fiscale = ocr.codice_fiscale.toUpperCase().replace(/\s+/g, '');
    if (ocr.indirizzo_residenza) payload.indirizzo = ocr.indirizzo_residenza;
    if (ocr.citta_residenza) payload.citta = ocr.citta_residenza;
    if (ocr.cap_residenza) payload.cap = ocr.cap_residenza;
    if (ocr.provincia_residenza) payload.provincia = (ocr.provincia_residenza || '').toUpperCase().slice(0, 2);
    if (notePrefill) payload.note = notePrefill;

    if (Object.keys(payload).length === 0) {
      toast.info('Nessun campo compilabile dai dati OCR');
      return;
    }

    if (!window.confirm(`Compilare la scheda cliente con ${Object.keys(payload).length} campi estratti dal documento? I campi gia' presenti verranno SOVRASCRITTI.`)) return;

    setBusy(true);
    try {
      await axios.put(`${API_URL}/api/clients/${clientId}`, payload);
      toast.success('Scheda cliente aggiornata');
      onClientFilled && onClientFilled(payload);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiornamento cliente');
    } finally {
      setBusy(false);
    }
  };

  const visibleFields = FIELDS.filter((f) => {
    const v = (editing ? draft : ocr)?.[f.key];
    return editing || (v !== undefined && v !== null && v !== '');
  });

  return (
    <Card className="border-sky-200 dark:border-sky-800 bg-sky-50/40 dark:bg-sky-950/20" data-testid={`ocr-card-${doc.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-1.5 rounded-md bg-sky-100 dark:bg-sky-900/40 shrink-0">
              <ScanText className="w-4 h-4 text-sky-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate flex items-center gap-1.5">
                Dati estratti con AI
                <Sparkles className="w-3.5 h-3.5 text-sky-500" />
              </p>
              <p className="text-[11px] text-slate-500 truncate">{doc.nome_file}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {status === 'done' && ocr?.confidenza && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${CONF_STYLE[ocr.confidenza] || CONF_STYLE.media}`}>
                Confidenza {ocr.confidenza}
              </span>
            )}
            {status === 'processing' && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300 font-semibold flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Analisi in corso
              </span>
            )}
            {status === 'error' && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-300 font-semibold flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Errore
              </span>
            )}
          </div>
        </div>

        {status === 'processing' ? (
          <div className="text-xs text-slate-500 py-6 text-center flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            L'AI sta leggendo il documento… attendi qualche secondo
          </div>
        ) : status === 'error' || (!ocr && status !== 'done') ? (
          <div className="text-center py-4">
            <p className="text-xs text-slate-500 mb-3">Nessun dato estratto. Prova a lanciare l'analisi.</p>
            <Button
              size="sm"
              onClick={handleTriggerOcr}
              disabled={busy}
              className="bg-sky-600 hover:bg-sky-700"
              data-testid={`ocr-trigger-${doc.id}`}
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ScanText className="w-4 h-4 mr-1.5" />}
              Analizza documento
            </Button>
          </div>
        ) : ocr ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {visibleFields.map((f) => {
                const value = editing ? draft[f.key] : ocr[f.key];
                return (
                  <div key={f.key} className="bg-white dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700 p-2">
                    <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                      {f.label}
                    </Label>
                    {editing ? (
                      <Input
                        value={value ?? ''}
                        onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value || null })}
                        className="h-8 mt-1 text-sm"
                        data-testid={`ocr-input-${f.key}`}
                      />
                    ) : (
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate flex-1" title={String(value || '')}>
                          {value || <span className="text-slate-300 italic text-xs">vuoto</span>}
                        </span>
                        {value && (
                          <button
                            type="button"
                            onClick={() => copyToClipboard(value, f.label)}
                            className="p-1 rounded hover:bg-sky-100 text-sky-600 shrink-0"
                            title="Copia"
                            data-testid={`ocr-copy-${f.key}`}
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-sky-100 dark:border-sky-900">
              {editing ? (
                <>
                  <Button size="sm" onClick={handleSave} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">
                    {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Salva correzioni
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancel} disabled={busy}>
                    <X className="w-4 h-4 mr-1" /> Annulla
                  </Button>
                </>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={handleEdit} data-testid={`ocr-edit-${doc.id}`}>
                    <Edit className="w-4 h-4 mr-1" /> Modifica
                  </Button>
                  <Button size="sm" variant="outline" onClick={copyAll}>
                    <Copy className="w-4 h-4 mr-1" /> Copia tutto
                  </Button>
                  {clientId && (
                    <Button
                      size="sm"
                      onClick={handleFillClient}
                      disabled={busy}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`ocr-fill-client-${doc.id}`}
                    >
                      {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserCog className="w-4 h-4 mr-1" />}
                      Compila scheda cliente
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleTriggerOcr}
                    disabled={busy}
                    className="text-slate-500"
                    data-testid={`ocr-redo-${doc.id}`}
                  >
                    {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                    Rifai OCR
                  </Button>
                  <div className="ml-auto text-[10px] text-slate-400 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-emerald-500" />
                    {doc.ocr_source === 'manuale' ? 'Corretto manualmente' : 'Analizzato da Gemini AI'}
                  </div>
                </>
              )}
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default DocumentOcrCard;
